// Ship 6e — reusable tag chips + add/remove UI.
//
// Used by both the Leads drawer and the Contacts drawer.  Attached tags
// render as colored pill chips with × on hover.  "+ Add tag" opens a
// searchable dropdown of firm tags with an inline "Create new tag" fallback
// when the query doesn't match any existing entry.
//
// Contract:
//   <TagChipsSection
//     tags={item.tags || []}          // [{ id, name, color }]
//     canWrite={boolean}              // hides + button + × when false
//     onAdd={async tagId => void}     // called with the tag id after user picks
//     onRemove={async tagId => void}  // called after user confirms via useAsk
//     allowCreate={boolean}           // default true — inline-create when miss
//   />
//
// The parent owns the API calls + refetch — this component is purely UI.
// Uses `listTags` + `createTag` from services/api.js for the dropdown data;
// no server writes for attach/detach — those are the parent's job.

import { useEffect, useRef, useState } from 'react'
import { listTags, createTag } from '../services/api'
import { useToast } from '../context/ToastContext'
import { useAsk } from '../context/PromptProvider'

// Contrast-safe text on a hex background — dark for light colors, light
// for dark.  Simple YIQ luminance rule.
function textOn(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return '#111'
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 140 ? '#111' : '#fff'
}

export default function TagChipsSection ({ tags = [], canWrite = false, onAdd, onRemove, allowCreate = true }) {
  const { showToast } = useToast()
  const ask = useAsk()
  const [picking, setPicking] = useState(false)
  const [allTags, setAllTags] = useState([])
  const [loadingAll, setLoadingAll] = useState(false)
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const containerRef = useRef(null)

  // Lazy-load firm tag catalog on first open + when the dropdown mounts.
  useEffect(() => {
    if (!picking) return
    let cancelled = false
    setLoadingAll(true)
    listTags()
      .then(r => { if (!cancelled) setAllTags(r.data?.tags || r.data || []) })
      .catch(() => { if (!cancelled) setAllTags([]) })
      .finally(() => { if (!cancelled) setLoadingAll(false) })
    return () => { cancelled = true }
  }, [picking])

  // Close the dropdown on outside click / Escape.
  useEffect(() => {
    if (!picking) return
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPicking(false); setQuery('')
      }
    }
    const onKey = (e) => { if (e.key === 'Escape') { setPicking(false); setQuery('') } }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [picking])

  const attachedIds = new Set(tags.map(t => t.id))
  const q = query.trim().toLowerCase()
  const available = allTags.filter(t =>
    !attachedIds.has(t.id) && (!q || (t.name || '').toLowerCase().includes(q))
  )
  const exactMatch = q && allTags.some(t => (t.name || '').toLowerCase() === q)

  const pickExisting = async (tag) => {
    setPicking(false); setQuery('')
    try {
      await onAdd?.(tag.id)
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to add tag', 'error')
    }
  }

  const createAndAttach = async () => {
    if (!q || creating) return
    setCreating(true)
    try {
      const r = await createTag({ name: query.trim() })
      const newTag = r.data?.tag || r.data
      if (!newTag?.id) throw new Error('Malformed response')
      // Merge into the local catalog so a re-open shows it in the list.
      setAllTags(prev => [...prev, newTag])
      showToast(`Created "${newTag.name}"`, 'success')
      await onAdd?.(newTag.id)
      setPicking(false); setQuery('')
    } catch (err) {
      const status = err.response?.status
      if (status === 409) {
        showToast('A tag with that name already exists', 'warning')
      } else if (status === 403) {
        showToast('You do not have permission to create tags', 'error')
      } else {
        showToast(err.response?.data?.error || 'Create tag failed', 'error')
      }
    } finally { setCreating(false) }
  }

  const confirmRemove = async (tag) => {
    const ok = await ask({
      type: 'confirm',
      title: `Remove tag "${tag.name}"?`,
      message: 'This unlinks the tag from this record but leaves the tag itself in place.',
      confirmLabel: 'Remove',
      danger: false,
    })
    if (!ok) return
    try {
      await onRemove?.(tag.id)
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to remove tag', 'error')
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5" ref={containerRef}>
      {tags.map(tag => {
        const bg = tag.color || '#6366f1'
        const fg = textOn(bg)
        return (
          <span
            key={tag.id}
            className="group inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: bg, color: fg }}
            title={`Tag: ${tag.name}`}
          >
            {tag.name}
            {canWrite && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); confirmRemove(tag) }}
                className="opacity-0 group-hover:opacity-100 transition-opacity leading-none hover:scale-125"
                title="Remove tag"
                aria-label={`Remove tag ${tag.name}`}
                style={{ color: fg }}
              >×</button>
            )}
          </span>
        )
      })}
      {tags.length === 0 && !canWrite && (
        <span className="text-xs text-gray-400 italic">No tags</span>
      )}
      {canWrite && (
        <div className="relative">
          {!picking ? (
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="text-xs text-gray-600 border border-dashed border-gray-300 hover:border-primary-400 hover:text-primary-700 rounded-full px-2 py-0.5"
              title="Attach a tag"
            >+ Add tag</button>
          ) : (
            <div className="absolute left-0 top-0 z-30 bg-white border border-gray-200 rounded-lg shadow-lg w-64 p-2">
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search or create tag…"
                className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 mb-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && q && !exactMatch && allowCreate) {
                    e.preventDefault()
                    createAndAttach()
                  }
                }}
              />
              <div className="max-h-56 overflow-y-auto">
                {loadingAll && (
                  <div className="text-xs text-gray-400 italic px-2 py-1">Loading tags…</div>
                )}
                {!loadingAll && available.length === 0 && !q && (
                  <div className="text-xs text-gray-400 italic px-2 py-1">
                    No firm tags yet.  Type a name above to create the first one.
                  </div>
                )}
                {!loadingAll && available.length === 0 && q && !allowCreate && (
                  <div className="text-xs text-gray-400 italic px-2 py-1">No matches.</div>
                )}
                {available.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => pickExisting(tag)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50"
                  >
                    <span
                      className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color || '#6366f1' }}
                    />
                    <span className="text-sm text-gray-800 truncate">{tag.name}</span>
                  </button>
                ))}
                {allowCreate && q && !exactMatch && (
                  <button
                    type="button"
                    onClick={createAndAttach}
                    disabled={creating}
                    className="w-full text-left flex items-center gap-2 px-2 py-1 rounded hover:bg-primary-50 border-t border-gray-100 mt-1 disabled:opacity-50"
                  >
                    <span className="text-primary-700 text-sm">+ Create "{query.trim()}"</span>
                  </button>
                )}
              </div>
              <div className="text-[10px] text-gray-400 mt-1 flex justify-between px-1">
                <span>Enter to create</span>
                <button
                  type="button"
                  onClick={() => { setPicking(false); setQuery('') }}
                  className="hover:text-gray-700"
                >Esc close</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
